import { twMerge } from "tailwind-merge";

import {
  useAssessedCountData,
  useAssessmentMode,
  useAssessmentNavData,
  useAssessmentPublishActions,
} from "../../utils/assessment";
import { useAv } from "../../utils/av";
import { useQueryParamsManager } from "../../utils/routing";

import { withActiveAv } from "components/av/AvSubsystemLoader";
import {
  SECONDARY_OVERLAY_QUERY_PARAM,
  SUBMISSION_ID_QUERY_PARAM,
  SecondaryOverlay,
} from "components/constants";
import { BaseFooter } from "components/footer/BaseFooter";
import EndClassMenu from "components/footer/EndClassMenu";
import { PublishActionButton } from "components/footer/assessment/PublishActionButton";
import { showUnpublishAssessmentModal } from "components/materials/page/assessment/helpers";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { Tooltip } from "mds/components/Tooltip";
import { RoundedButton, RoundedButtonProps } from "mds/components/buttons/RoundedButton";
import { useIsSmOrLarger } from "mds/hooks/use-responsive";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CopySendArrowIcon,
  ReactionTransparentVoteYesIcon,
} from "mds/icons";
import { useAssessmentGradingFormContext } from "providers/AssessmentGradingFormProvider";
import { useAppSelector } from "store/index";
import {
  selectCurrentClassUserIsInPersonClass,
  selectCurrentPage,
  selectCurrentSectionUserId,
  selectFullSubmissionById,
} from "store/selectors";

const LazyVideoMicControls = withActiveAv(() => import("components/av/VideoMicControls"));

export const AssessmentProgressBar = () => {
  const { isAiAssessmentLoading: isLoading, saveConfirmation } = useAssessmentGradingFormContext();
  const selectedPage = useAppSelector(selectCurrentPage);
  const { onBack, onNext, prevId, isLastPanel } = useAssessmentNavData(selectedPage);
  const sectionUserId = useAppSelector(selectCurrentSectionUserId);
  const { getQueryParam } = useQueryParamsManager();
  const { isInVirtualClass } = useAv();
  const isInInPersonClass = useAppSelector(selectCurrentClassUserIsInPersonClass);
  const isInClassSession = isInVirtualClass || isInInPersonClass;

  const { stopAssessing, onExitAssessmentModeWithModal } = useAssessmentMode(selectedPage);
  const { publishGrades, unpublishGrades } = useAssessmentPublishActions(selectedPage);
  const { count: officialSubmissionsCount, total: totalSubmissionsCount } =
    useAssessedCountData(selectedPage);

  const submissionId = getQueryParam(SUBMISSION_ID_QUERY_PARAM);
  const submission = useAppSelector((s) => selectFullSubmissionById(s, submissionId));

  const isNextDisabled = !sectionUserId || isLastPanel || isLoading;
  const isPrevDisabled = !sectionUserId || !prevId || isLoading;

  const assessedCountData = useAssessedCountData(selectedPage);
  const { count, total } = assessedCountData;

  const isMobile = !useIsSmOrLarger();

  // Early return after all hooks
  if (!selectedPage) return null;

  const percentage = total === 0 ? 0 : Math.ceil((count / total) * 100);
  const safePercentage = Math.min(100, Math.max(0, percentage));

  const submitBtnAttrs: Omit<RoundedButtonProps, "kind"> = {
    form: "assessment-grading-form",
    type: "submit",
    name: "submitSource",
  };
  const prevBtnAttrs: Omit<RoundedButtonProps, "kind"> = submission
    ? { ...submitBtnAttrs, value: "prev" }
    : { onClick: (e) => onBack(e as React.MouseEvent<HTMLButtonElement>) };
  const nextBtnAttrs: Omit<RoundedButtonProps, "kind"> = submission
    ? { ...submitBtnAttrs, value: "next" }
    : { onClick: onNext };

  const darkMode = isInClassSession;

  const onPublishGrades = async () => {
    await publishGrades();
    return stopAssessing({ [SECONDARY_OVERLAY_QUERY_PARAM]: SecondaryOverlay.AssessmentSummary });
  };

  const onUnpublishGrades = () => {
    showUnpublishAssessmentModal(officialSubmissionsCount, () => {
      unpublishGrades();
      stopAssessing();
    });
  };

  const onExitAssessmentMode = onExitAssessmentModeWithModal(
    officialSubmissionsCount,
    totalSubmissionsCount,
  );

  return (
    <BaseFooter darkMode={darkMode}>
      <LazyVideoMicControls />
      <div
        className={twMerge(
          "flex w-full items-center justify-center gap-3 text-sm font-medium text-black-tint-40",
          darkMode && "text-white",
        )}
      >
        <PublishActionButton
          isMobile={isMobile}
          page={selectedPage}
          sectionUserId={sectionUserId}
          onPublishGrades={onPublishGrades}
          onUnpublishGrades={onUnpublishGrades}
        />

        <RoundedButton
          className={
            saveConfirmation?.source === "prev"
              ? "cursor-default disabled:border-green-shade-20 disabled:bg-green-shade-20 disabled:text-white"
              : undefined
          }
          disabled={isPrevDisabled}
          eventName="Assessment - Previous"
          eventProps={{ pageId: selectedPage.id, submissionId: submission?.id }}
          kind={saveConfirmation?.source === "prev" ? "success" : "secondary"}
          {...prevBtnAttrs}
        >
          <span className="relative">
            <span className={saveConfirmation?.source === "prev" ? "invisible" : undefined}>
              <IconText
                iconStart={!isMobile ? <ChevronLeftIcon /> : undefined}
                text={t("common.previous")}
              />
            </span>
            {saveConfirmation?.source === "prev" && (
              <span className="animate-save-fade absolute inset-0 flex items-center justify-center gap-2">
                <ReactionTransparentVoteYesIcon className="text-white" />
                {t("assessment.saved")}
              </span>
            )}
          </span>
        </RoundedButton>

        <Tooltip
          text={t("assessment.assessment_progress_bar_tooltip", {
            count: officialSubmissionsCount,
            total: totalSubmissionsCount,
          })}
        >
          <div
            className={twMerge(
              "flex items-center justify-end whitespace-nowrap",
              !isMobile && "w-[60px]",
            )}
          >
            {t("common.index_of_total_counter", { index: count, total })}
          </div>
        </Tooltip>

        {!isMobile && (
          <>
            <div className="flex w-full max-w-[1000px] flex-grow">
              <div className="h-3 w-full rounded-full bg-green-tint-70">
                <div
                  className="duration-400 h-3 rounded-full bg-green-shade-20 transition-[width] ease-in-out"
                  style={{ width: `${safePercentage}%` }}
                />
              </div>
            </div>

            <div className="flex w-[40px] items-center justify-start">
              {t("common.percentage", { number: safePercentage })}
            </div>
          </>
        )}

        <Tooltip
          text={!sectionUserId ? t("assessment_card.cannot_assess_without_enrollment") : undefined}
        >
          <RoundedButton
            className={
              saveConfirmation?.source === "next"
                ? "min-w-28 cursor-default disabled:border-green-shade-20 disabled:bg-green-shade-20 disabled:text-white"
                : "min-w-28"
            }
            disabled={isNextDisabled}
            eventName="Assessment - Next"
            eventProps={{ pageId: selectedPage.id, submissionId: submission?.id }}
            kind="success"
            {...nextBtnAttrs}
          >
            <span className="relative">
              <span className={saveConfirmation?.source === "next" ? "invisible" : undefined}>
                <IconText
                  iconEnd={!isMobile ? <ChevronRightIcon /> : undefined}
                  text={t("common.next")}
                />
              </span>
              {saveConfirmation?.source === "next" && (
                <span className="animate-save-fade absolute inset-0 flex items-center justify-center gap-2">
                  <ReactionTransparentVoteYesIcon className="text-white" />
                  {t("assessment.saved")}
                </span>
              )}
            </span>
          </RoundedButton>
        </Tooltip>

        <RoundedButton
          eventName="Assessment - Stop"
          eventProps={{
            pageId: selectedPage.id,
            pageTitle: selectedPage.title,
            submissionId: submission?.id,
          }}
          kind="secondary"
          onClick={onExitAssessmentMode}
        >
          <IconText
            iconStart={<CopySendArrowIcon />}
            text={!isMobile ? t("assessment.finish_later") : undefined}
          />
        </RoundedButton>
      </div>
      {isInClassSession && (
        <div className="pl-2">
          <EndClassMenu />
        </div>
      )}
    </BaseFooter>
  );
};
